import {SaveState} from '@deliveriesondemand/dod-data';
import {parseAddress} from '@deliveriesondemand/dod-utils';
import {
    saveSSN,
    requestCallback,
    manualEntry,
    submitFinal,
    createOffer,
    explicitSave,
    save,
    sendErrorToDevs,
    sessionPoll
} from '-/api/api';
import mixpanel from 'mixpanel-browser';
import qs from 'query-string';

import Service from './Service';
import BuyerInfoService from '-/services/BuyerInfoService';
import CreditorService from '-/services/CreditorService';
import DealInfoService from '-/services/DealInfoService';
import NavigationService from '-/services/NavigationService';
import EmploymentInfoService from '-/services/EmploymentInfoService';
import VehicleInfoService from '-/services/VehicleInfoService';
import AdditionalIncomeService from '-/services/AdditionalIncomeService';
import SessionService from '-/services/SessionService';
import UIService from '-/services/UIService';
import FunnelService from '-/services/FunnelService';
import ToastService from '-/services/ToastService';
import {collectErrors, renderError} from '-/util/error';
import CallbackService from '-/services/CallbackService';
import {UPDATE_ID, SET_CONFIG, SET_CONFIG_KEY} from '-/actions/types';
import config from '-/config/config';
import {get} from '-/util/lodash';
import {getOfferId, isNewOffer, setMyOffers} from '-/util/util';

const {appTitle} = config;
const API_SAVE_STATE = 'API_SAVE_STATE';
const API_SET_ERRORS = 'API_SET_ERRORS';
const initialState = {
    saveState: 'NotSaved',
    saveStateExplicit: 'NotSaved',
    saveStateFinalSubmit: 'NotSaved'
};

const reducer = (state = initialState, action) => {
    const {type, payload} = action;
    switch (type) {
        case API_SAVE_STATE:
            return {
                ...state,
                [payload.key]: payload.saveState
            };
        case API_SET_ERRORS:
            return {
                ...state,
                errors: payload
            };
    }
    return state;
};

// The new-offer route has no offer until createOffer returns, so everything the member
// did up to this point was recorded against a per-browser visitor id. Join the two
// identities before the reload, and flush the funnel events that were held back waiting
// for an offer to attach to.
const adoptCreatedOffer = offerId => {
    try {
        const previousId = mixpanel.get_distinct_id && mixpanel.get_distinct_id();

        if (previousId && previousId !== offerId) {
            mixpanel.alias(offerId, previousId);
        }

        mixpanel.identify(offerId);
        mixpanel.register({hasOffer: true, offerId});
        mixpanel.people.set({'Offer Id': offerId});
    } catch (ex) {
        // Analytics must never block offer creation.
    }

    FunnelService.setOfferId(offerId);
};

class ApiService extends Service {
    constructor() {
        super();

        if (!ApiService.instance) {
            ApiService.instance = this;
        }

        return ApiService.instance;
    }

    initSessionPoll() {
        setInterval(sessionPoll, 20000);

        sessionPoll();
    }

    getReducerName() {
        return 'api';
    }

    getReducer() {
        return reducer;
    }

    setSaveState(saveState, key = 'saveState') {
        this.store.dispatch({
            type: API_SAVE_STATE,
            payload: {saveState, key}
        });
    }

    sendManualEntry(formInfo) {
        return new Promise((resolve, reject) => {
            manualEntry(formInfo).then(() => {
                return resolve();
            }).catch(err => {
                ToastService.addToastWithDuration(ToastService.DANGER, 5000, renderError(err));
                return reject();
            });
        });
    }

    saveCallbackTimes() {
        const {offerId} = this.getState().config;
        const {phone, selected: time, phoneType, timezone} = CallbackService.getDataForSave();
        const obj = {offerId, phone, time, phoneType, timezone};
        return new Promise((resolve, reject) => {
            requestCallback(obj).then(() => {
                return resolve();
            }).catch(err => {
                ToastService.addToastWithDuration(ToastService.DANGER, 5000, renderError(err));
                return reject();
            });
        });
    }

    saveSSN(data) {
        const {offerId} = this.getState().config;
        const {app: appSSNPart, coApp: coAppSSNPart} = data;
        const obj = {offerId, appSSNPart, coAppSSNPart};

        return new Promise((resolve, reject) => {
            saveSSN(obj).then(() => {
                return resolve();
            }).catch(err => {
                ToastService.addToastWithDuration(ToastService.DANGER, 5000, renderError(err));
                return reject();
            });
        });
    }

    transformAddress = address => {
        const addressParts = parseAddress(address.street1);
        const {street1, city, state = '', zip} = addressParts;

        return {
            street1,
            city,
            state,
            zip
        };
    };

    prepareStateForSubmission(parts = []) {
        let stips;
        const {offerId} = this.getState().config;
        const vehicleInfo = VehicleInfoService.getDataForSave();
        const navigation = NavigationService.getDataForSave();
        const additionalIncome = AdditionalIncomeService.getDataForSave();
        const buyerInfo = BuyerInfoService.getDataForSave();
        const dealInfo = DealInfoService.getDataForSave();
        const employmentInfo = EmploymentInfoService.getDataForSave();
        const stepsCompleted = UIService.getDataForSave();

        const {
            buyerInfo: {
                address = {},
                email,
                cellphone
            } = {}
        } = buyerInfo;
        const employmentStatus = get(employmentInfo, 'employmentInfo.status');

        const {extraStipTransportType} = vehicleInfo;
        const {hasVinReminder = false, hasRegReminder = false} = navigation;

        if (extraStipTransportType) {
            const isEmail = extraStipTransportType === 'EMAIL';
            const isSms = extraStipTransportType === 'SMS';
            // set stips properties
            stips = {
                app: {
                    request: {
                        ...isEmail && {
                            viaEmail: true,
                            email
                        },
                        ...isSms && {
                            viaSms: true,
                            phone: cellphone
                        }
                    }
                }
            };
            // set setVinReminder in navigation
            navigation.setVinReminder = hasVinReminder;
            navigation.setRegReminder = hasRegReminder;
        }

        // Geo Field returns an object `street1` as the only key if the address
        // was not selected from the field's suggestions. Here, we check for only one
        // key and the existence of `street1`.
        const shouldAddressTransform = Object.keys(address).length === 1 && address.street1;

        if (shouldAddressTransform) {
            buyerInfo.buyerInfo.address = this.transformAddress(address);
        }

        let extraData = {};

        if (!DealInfoService.isCashDeal()) {
            extraData = {
                ...additionalIncome,
                ...employmentInfo
            };
        }

        let obj = {
            offerId,
            navigation,
            vehicleInfo,
            dealInfo,
            ...buyerInfo,
            ...stepsCompleted,
            ...stips && {
                stips
            },
            ...extraData
        };

        if (!offerId) {
            const salespersonId = localStorage.getItem('salesperson');

            localStorage.removeItem('salesperson');

            const {salespeople = []} = CreditorService.getReducerState();
            const salesperson = salespeople.find(item => item.dmsId === salespersonId);

            if (salesperson) {
                const {dmsId, fullName: name} = salesperson;

                obj.salespersonAssignment = {
                    dmsId,  
                    name
                };
            }
        }

        const hasPart = matcher => {
            matcher = Array.isArray(matcher) ? matcher : [matcher];

            return parts.some(part => matcher.includes(part));
        };
        const hasNonePart = hasPart('none');

        if (parts.length) {
            const isRetirement = hasPart('employmentInfo') && employmentStatus === 'retired';

            if (!hasPart(['buyerInfo', 'citizenship', 'priorMembership']) || hasNonePart) {
                delete obj.buyerInfo;
                delete obj.buyerInfo_CoApp;
            }
            if (!hasPart('vehicleInfo') || hasNonePart) {
                delete obj.vehicleInfo;
                delete obj.stips;
            }
            if (!hasPart('employmentInfo') || hasNonePart) {
                delete obj.employmentInfo;
                delete obj.employmentInfo_CoApp;
            }
            if ((!hasPart('additionalIncome') || hasNonePart) && !isRetirement) {
                delete obj.additionalIncome;
                delete obj.additionalIncome_CoApp;
            }
            if (!hasPart('dealInfo') || hasNonePart) {
                delete obj.dealInfo;
            }
        }

        return obj;
    }

    async save(parts) {
        parts = parts ? Array.isArray(parts) ? parts : [parts] : [];

        const state = this.prepareStateForSubmission(parts);
        const {offerId} = state;
        this.setSaveState(SaveState.Saving);

        if (!offerId) {
            const queryParams = qs.parse(location.search);
            const {creditorId} = queryParams;
            const dealTypeRaw = getOfferId();
            const dealType = dealTypeRaw === 'new' ? 'finance' : dealTypeRaw;
            const formData = {
                ...state,
                ...dealType && {dealType},
                ...creditorId && {creditorId}
            };

            try {
                const results = await createOffer(formData);
                const {data} = results;

                this.setSaveState(SaveState.Saved);
                this.setErrors([]);
                setTimeout(() => this.setSaveState(SaveState.NotSaved), 1000);

                const {offerId, updates} = data;
                const updatedFields = Object.keys(updates);
                const currentLocation = window.location.href.replace(/offerId=(cash|finance|new)/, `offerId=${offerId}`);

                adoptCreatedOffer(offerId);
                history.pushState({}, appTitle, currentLocation);

                this.store.dispatch({
                    type: UPDATE_ID,
                    value: offerId
                });

                UIService.initialize('INPROGRESS');
                DealInfoService.set('dealType', dealType);
                
                updatedFields.forEach(field => {
                    if (field === 'isRateDropPromoActive') {
                        this.setKey('isRateDropPromoActive', updates[field]);
                    }
                });

                if (!SessionService.isCCRMode()) {
                    setMyOffers(offerId);
                }

                return;
            } catch (err) {
                this.setSaveState(SaveState.Failed);
                this.setErrors(collectErrors(err));
                
                if (!parts.length || parts.includes('none')) {
                    ToastService.addToastWithDuration(ToastService.DANGER, 5000, renderError(err));
                }
                
                throw new Error(err);
            }
        } else {
            try {
                await save(state);

                this.setSaveState(SaveState.Saved);
                this.setErrors([]);
                setTimeout(() => this.setSaveState(SaveState.NotSaved), 1000);

                return;
            } catch (err) {
                this.setSaveState(SaveState.Failed);
                this.setErrors(collectErrors(err));
                // if we are saving from a form screen, any error will
                // show as a validation error in that screen.
                if (!parts.length || parts.includes('none')) {
                    ToastService.addToastWithDuration(ToastService.DANGER, 5000, renderError(err));
                }
                
                throw new Error(err);
            }
        }
    }

    resetStips() {
        this.setSaveState(SaveState.Saving, 'saveStateExplicit');

    }

    async explicitSave(parts) {
        parts = parts ? Array.isArray(parts) ? parts : [parts] : [];
        const state = this.prepareStateForSubmission(parts);
        const {offerId} = state;
        this.setSaveState(SaveState.Saving, 'saveStateExplicit');

        if (!offerId) {
            const queryParams = qs.parse(location.search);
            const {creditorId} = queryParams;
            const offerId = getOfferId();
            const formData = {
                ...state,
                ...offerId && {
                    dealType: offerId === 'new' ? 'finance' : offerId
                },
                ...creditorId && {creditorId}
            };
            const isNew = isNewOffer(offerId);

            if (!isNew) {
                const error = `Deal type is not valid: ${offerId}`;
                this.setSaveState(SaveState.Failed, 'saveStateExplicit');
                this.setErrors([error]);

                throw new Error(error);
            }

            try {
                const results = await createOffer(formData);
                const {data} = results;
                const {offerId} = data;
                const currentLocation = window.location.href.replace(/offerId=(cash|finance|new)/, `offerId=${offerId}`);

                adoptCreatedOffer(offerId);
                this.setErrors([]);
                
                if (!SessionService.isCCRMode()) {
                    setMyOffers(offerId);
                }
                
                history.pushState({}, appTitle, currentLocation);
                location.reload();
            } catch (err) {
                this.setSaveState(SaveState.Failed);
                this.setErrors(collectErrors(err));
                
                if (!parts.length || parts.includes('none')) {
                    ToastService.addToastWithDuration(ToastService.DANGER, 5000, renderError(err));
                }

                throw new Error(err);
            }
        } else {
            try {
                await explicitSave(state);

                this.setSaveState(SaveState.Saved, 'saveStateExplicit');
                this.setErrors([]);
                // to ensure the save animation plays on the button,
                // we add a timeout here
                setTimeout(() => {
                    // for improved ux we give a little delay from
                    // when the button goes back to it's original state,
                    // and the page change occurs
                    setTimeout(() => {
                        NavigationService.startOver();
                        this.setSaveState(SaveState.NotSaved, 'saveStateExplicit');
                    }, 500);
                }, 1000);
            } catch (err) {
                this.setSaveState(SaveState.Failed, 'saveStateExplicit');
                this.setErrors(collectErrors(err));

                ToastService.addToastWithDuration(ToastService.DANGER, 5000, renderError(err));

                throw new Error(err);
            }
        }
    }

    getErrors() {
        return this.getReducerState().errors || [];
    }

    submitFinal(data = {}) {
        const state = this.prepareStateForSubmission();
        const finalData = {
            ...state,
            ...data
        };

        this.setSaveState(SaveState.Saving, 'saveStateFinalSubmit');
        return new Promise((resolve, reject) => {
            submitFinal(finalData).then(res => {
                this.setSaveState(SaveState.Saved, 'saveStateFinalSubmit');
                setTimeout(() => this.setSaveState(SaveState.NotSaved, 'saveStateFinalSubmit'), 1000);
                resolve(res);
            }).catch(err => {
                this.setSaveState(SaveState.Failed, 'saveStateFinalSubmit');
                ToastService.addToastWithDuration(ToastService.DANGER, 5000, renderError(err));
                reject(err);
            });
        });
    }

    sendError(errorInfoObj) {
        sendErrorToDevs(errorInfoObj);
    }

    setKey(key, value) {
        this.store.dispatch({
            type: SET_CONFIG_KEY,
            value: {
                key,
                value
            }
        });
    }

    setConfig(config = {}) {
        this.store.dispatch({
            type: SET_CONFIG,
            value: config
        });
    }

    setErrors = errors => {
        this.store.dispatch({
            type: API_SET_ERRORS,
            payload: errors
        });
    }
}

export default new ApiService();
